import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Panel,
  Form,
  FormGroup,
  ControlLabel,
  Input,
  SelectPicker,
  Button,
  Message,
  Loader
} from 'rsuite';
import styled from 'styled-components';
import {
  requestOtp,
  verifyOtp,
  forgotPassword
} from '../../redux/sagas/mfa-auth/routines';
import { resetMfaAuth } from '../../redux/actions';
import {
  getIsRequesting,
  getError,
  getInfo,
  getMfaRequired,
  getMaskedEmail
} from '../../redux/selectors/mfa-auth';

const STEP_CREDENTIALS = 'credentials';
const STEP_OTP = 'otp';
const STEP_FORGOT = 'forgot';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 40px;
  padding-bottom: 40px;
`;

const LoginPanel = styled(Panel)`
  width: 380px;
  max-width: 100%;
`;

const StyledMessage = styled(Message)`
  margin-bottom: 16px;
`;

const Intro = styled.p`
  margin-bottom: 16px;
`;

const getLocationData = () => {
  const fbmLogin = window.fbmLoginConfig || {};
  const locations = fbmLogin.locations || {};
  return Object.keys(locations).map(id => ({
    value: Number(id),
    label: locations[id]
  }));
};

const Login = () => {
  const dispatch = useDispatch();
  const locationData = useMemo(getLocationData, []);
  const requesting = useSelector(getIsRequesting);
  const error = useSelector(getError);
  const info = useSelector(getInfo);
  const mfaRequired = useSelector(getMfaRequired);
  const maskedEmail = useSelector(getMaskedEmail);

  const [step, setStep] = useState(STEP_CREDENTIALS);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [location, setLocation] = useState(
    locationData.length ? locationData[0].value : 0
  );
  const [code, setCode] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(
    () => {
      if (mfaRequired) {
        setStep(STEP_OTP);
      }
    },
    [mfaRequired]
  );

  const clearMessages = () => {
    setValidationError('');
  };

  const submitCredentials = () => {
    clearMessages();
    if (!username.trim() || !password.trim()) {
      setValidationError('Please enter your username and password.');
      return;
    }
    dispatch(requestOtp({ username, password, location }));
  };

  const submitOtp = () => {
    clearMessages();
    if (!code.trim()) {
      setValidationError('Please enter the verification code.');
      return;
    }
    dispatch(verifyOtp({ username, location, code }));
  };

  const resendCode = () => {
    clearMessages();
    dispatch(requestOtp({ username, password, location }));
  };

  const backToCredentials = () => {
    setStep(STEP_CREDENTIALS);
    setCode('');
    clearMessages();
    dispatch(resetMfaAuth());
  };

  const goToForgotPassword = () => {
    setStep(STEP_FORGOT);
    clearMessages();
    dispatch(resetMfaAuth());
  };

  const submitForgotPassword = () => {
    clearMessages();
    if (!username.trim()) {
      setValidationError('Please enter your username.');
      return;
    }
    dispatch(forgotPassword({ username }));
  };

  const displayError = validationError || error;

  return (
    <LoginContainer>
      <LoginPanel header={<h3>Login</h3>} bordered>
        {displayError && (
          <StyledMessage type="error" description={displayError} />
        )}
        {info &&
          !displayError && <StyledMessage type="info" description={info} />}

        {step === STEP_CREDENTIALS && (
          <Form fluid>
            <FormGroup>
              <ControlLabel>Username</ControlLabel>
              <Input
                value={username}
                onChange={setUsername}
                disabled={requesting}
              />
            </FormGroup>
            <FormGroup>
              <ControlLabel>Password</ControlLabel>
              <Input
                type="password"
                value={password}
                onChange={setPassword}
                disabled={requesting}
              />
            </FormGroup>
            {locationData.length > 0 && (
              <FormGroup>
                <ControlLabel>Location</ControlLabel>
                <SelectPicker
                  data={locationData}
                  value={location}
                  onChange={setLocation}
                  cleanable={false}
                  searchable={false}
                  block
                  disabled={requesting}
                />
              </FormGroup>
            )}
            <FormGroup>
              <Button
                appearance="primary"
                color="green"
                block
                onClick={submitCredentials}
                disabled={requesting}
              >
                {requesting ? <Loader content="Please wait..." /> : 'Login'}
              </Button>
            </FormGroup>
            <FormGroup>
              <Button
                appearance="link"
                onClick={goToForgotPassword}
                disabled={requesting}
              >
                Forgot password?
              </Button>
            </FormGroup>
          </Form>
        )}

        {step === STEP_FORGOT && (
          <Form fluid>
            <Intro>
              Enter your username and we&apos;ll email a password reset link to
              your registered email address.
            </Intro>
            <FormGroup>
              <ControlLabel>Username</ControlLabel>
              <Input
                value={username}
                onChange={setUsername}
                disabled={requesting}
              />
            </FormGroup>
            <FormGroup>
              <Button
                appearance="primary"
                color="green"
                block
                onClick={submitForgotPassword}
                disabled={requesting}
              >
                {requesting ? (
                  <Loader content="Sending..." />
                ) : (
                  'Send reset link'
                )}
              </Button>
            </FormGroup>
            <FormGroup>
              <Button
                appearance="link"
                onClick={backToCredentials}
                disabled={requesting}
              >
                Back to login
              </Button>
            </FormGroup>
          </Form>
        )}

        {step === STEP_OTP && (
          <Form fluid>
            <Intro>
              Enter the verification code
              {maskedEmail ? ` sent to ${maskedEmail}` : ''}.
            </Intro>
            <FormGroup>
              <ControlLabel>Verification Code</ControlLabel>
              <Input
                value={code}
                onChange={setCode}
                disabled={requesting}
                placeholder="Enter 6-digit code"
              />
            </FormGroup>
            <FormGroup>
              <Button
                appearance="primary"
                color="green"
                block
                onClick={submitOtp}
                disabled={requesting}
              >
                {requesting ? (
                  <Loader content="Verifying..." />
                ) : (
                  'Verify & Login'
                )}
              </Button>
            </FormGroup>
            <FormGroup>
              <Button
                appearance="link"
                onClick={resendCode}
                disabled={requesting}
              >
                Resend code
              </Button>
              <Button
                appearance="link"
                onClick={backToCredentials}
                disabled={requesting}
              >
                Back to login
              </Button>
            </FormGroup>
          </Form>
        )}
      </LoginPanel>
    </LoginContainer>
  );
};

export default Login;
